/**
 * Center pane — server-rendered iframe at ``/api/decks/{id}/render``.
 *
 * Reload is keyed on the deck's ``cacheTag``: each successful save bumps
 * the tag, the parent re-renders, and the iframe ``src`` query string
 * changes, triggering a navigation in the iframe.
 */

import { useEffect, useRef, useState } from "react";

import { deckRenderUrl } from "../api";

interface PreviewFrameProps {
  deckId: string;
  cacheTag: string;
}

export function PreviewFrame({ deckId, cacheTag }: PreviewFrameProps) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [loadKey, setLoadKey] = useState(cacheTag);

  // Bump the load key when cacheTag changes — re-renders the iframe.
  useEffect(() => {
    setLoadKey(cacheTag);
  }, [cacheTag]);

  return (
    <section style={wrapperStyle} data-testid="preview-frame-wrapper">
      <header style={headerStyle}>
        <span style={{ color: "var(--gray-9)", fontSize: "0.85em" }}>
          Preview
        </span>
        <span style={{ color: "var(--gray-9)", fontSize: "0.75em" }}>
          cacheTag = <code>{cacheTag}</code>
        </span>
      </header>
      <iframe
        ref={ref}
        key={loadKey}
        title="Deck preview"
        src={deckRenderUrl(deckId, cacheTag)}
        style={iframeStyle}
        data-testid="deck-preview-iframe"
      />
    </section>
  );
}

const wrapperStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  background: "var(--gray-2)",
  borderRight: "1px solid var(--gray-5)",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 0.75rem",
  borderBottom: "1px solid var(--gray-5)",
  background: "var(--gray-1)",
};

const iframeStyle: React.CSSProperties = {
  flex: 1,
  width: "100%",
  border: "none",
  background: "white",
};
